import React, { useEffect, useMemo, useState } from 'react';

const pickName = (obj, locale) => {
  if (!obj) return '';
  return obj?.[`name_${locale}`] || obj?.name || '';
};

const toIdStr = (v) => (v == null ? '' : String(v));

const asArray = (v) => (Array.isArray(v) ? v : []);

export default function GroupGeneralInfoEditor({
  open,
  group,
  locale = 'ru',
  t,
  axios,
  refs,
  refsLoading = false,
  loadAllRefs,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    subject_id: '',
    grade_id: '',
    education_level_id: '',
    direction_id: '',
    development_language_id: '',
    teaching_language_id: '',
    publication_type_id: '',
    service_type_id: '',
    submission_year_id: '',
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    if (typeof loadAllRefs === 'function') loadAllRefs();
  }, [open, loadAllRefs]);

  useEffect(() => {
    if (!open || !group) return;

    setErr('');
    setForm({
      subject_id: toIdStr(group.subject_id ?? group.subject?.id),
      grade_id: toIdStr(group.grade_id ?? group.grade?.id),
      education_level_id: toIdStr(group.education_level_id ?? group.education_level?.id),
      direction_id: toIdStr(group.direction_id ?? group.direction?.id),
      development_language_id: toIdStr(group.development_language_id ?? group.development_language?.id),
      teaching_language_id: toIdStr(group.teaching_language_id ?? group.teaching_language?.id),
      publication_type_id: toIdStr(group.publication_type_id ?? group.publication_type?.id),
      service_type_id: toIdStr(group.service_type_id ?? group.service_type?.id),
      submission_year_id: toIdStr(group.submission_year_id ?? group.submission_year?.id),
    });
  }, [open, group]);

  const r = useMemo(
    () => ({
      subjects: asArray(refs?.subjects),
      grades: asArray(refs?.grades),
      educationLevels: asArray(refs?.educationLevels),
      directions: asArray(refs?.directions),
      developmentLanguages: asArray(refs?.developmentLanguages),
      teachingLanguages: asArray(refs?.teachingLanguages),
      publicationTypes: asArray(refs?.publicationTypes),
      serviceTypes: asArray(refs?.serviceTypes),
      submissionYears: asArray(refs?.submissionYears),
    }),
    [refs]
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErr('');
  };

  const save = async () => {
  if (!group?.id) return;

  const payload = {
    title: group.title || '',
    subject_id: Number(form.subject_id) || null,
    grade_id: Number(form.grade_id) || null,
    education_level_id: Number(form.education_level_id) || null,
    direction_id: Number(form.direction_id) || null,
    development_language_id: Number(form.development_language_id) || null,
    teaching_language_id: Number(form.teaching_language_id) || null,
    publication_type_id: Number(form.publication_type_id) || null,
    service_type_id: Number(form.service_type_id) || null,
    submission_year_id: Number(form.submission_year_id) || null,
  };

  try {
    setSaving(true);
    setErr('');

    const res = await axios.put(`/publisher/groups/${group.id}`, payload, {
      headers: {
        'Accept-Language': locale,
        'Content-Type': 'application/json',
      },
    });

// response нам почти не помогает, потому что связей (subject/grade/...) нет
// поэтому передаём наверх payload, а наверху уже обогатим через refs + сделаем refreshOneGroup
await onSaved?.(null, payload);

onClose?.();
  } catch (e) {
    setErr(
      e?.response?.data?.message ||
        t?.saveErr ||
        (locale === 'ru' ? 'Ошибка при сохранении' : 'Сақтау кезінде қате')
    );
  } finally {
    setSaving(false);
  }
};

  if (!open || !group) return null;

  const SelectRow = ({ label, value, onChange, options, placeholder = '—' }) => (
  <label className="ggi-field">
    <span className="ggi-field__label">{label}</span>
    <select
      className="ggi-field__control"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      disabled={saving || refsLoading}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.id} value={String(opt.id)}>
          {pickName(opt, locale) || `#${opt.id}`}
        </option>
      ))}
    </select>
  </label>
);

return (
  <div
    className="ggi-editor"
    onClick={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    onPointerDown={(e) => e.stopPropagation()}
  >
      <div className="ggi-editor__grid">
        <SelectRow
          label={t.groupInfoSubject}
          value={form.subject_id}
          onChange={(v) => setField('subject_id', v)}
          options={r.subjects}
        />

        <SelectRow
          label={t.groupInfoGrade}
          value={form.grade_id}
          onChange={(v) => setField('grade_id', v)}
          options={r.grades}
        />

        <SelectRow
          label={t.groupInfoEduLevel}
          value={form.education_level_id}
          onChange={(v) => setField('education_level_id', v)}
          options={r.educationLevels}
        />

        <SelectRow
          label={t.groupInfoDirection}
          value={form.direction_id}
          onChange={(v) => setField('direction_id', v)}
          options={r.directions}
        />

        <SelectRow
          label={t.groupInfoDevLang}
          value={form.development_language_id}
          onChange={(v) => setField('development_language_id', v)}
          options={r.developmentLanguages}
        />

        <SelectRow
          label={t.groupInfoTeachLang}
          value={form.teaching_language_id}
          onChange={(v) => setField('teaching_language_id', v)}
          options={r.teachingLanguages}
        />

        <SelectRow
          label={t.groupInfoPubType}
          value={form.publication_type_id}
          onChange={(v) => setField('publication_type_id', v)}
          options={r.publicationTypes}
        />

        <SelectRow
          label={t.groupInfoServiceType}
          value={form.service_type_id}
          onChange={(v) => setField('service_type_id', v)}
          options={r.serviceTypes}
        />

        <SelectRow
          label={t.groupInfoSubmissionYear}
          value={form.submission_year_id}
          onChange={(v) => setField('submission_year_id', v)}
          options={r.submissionYears}
        />
      </div>

      {err ? <div className="ggi-editor__error">{err}</div> : null}

      <div className="ggi-editor__actions">
        <button type="button" className="gl-btn" onClick={save} disabled={saving || refsLoading}>
          {saving ? '...' : t.save}
        </button>
        <button type="button" className="gl-btn gl-btn--ghost" onClick={onClose} disabled={saving}>
          {t.cancel}
        </button>
      </div>
    </div>
  );
}